(function () {
  let logger;  
  function createLogger(defaultLevel = "debug") {
    // Logging levels in order of increasing severity
    const LOG_LEVELS = ["debug", "info", "success", "warn", "error"];
    // Map for quick lookup of level index
    const LOG_LEVEL_INDEX = LOG_LEVELS.reduce((acc, lvl, idx) => { acc[lvl] = idx; return acc; }, {});

    const baseStyles = [
      "color: #fff",
      "background-color: #2759e1",
      "padding: 2px 8px",
      "border-radius: 4px",
    ].join(";");

    const logger = {};

    function loggerBase(level, ...args) {
      if (!window.console) return;

      let style = baseStyles;
      switch (level) {
        case "debug":
          style = "color: #fff; background-color: #6c757d; padding: 2px 8px; border-radius: 4px;";
          break;
        case "info":
          style = "color: #fff; background-color: #2759e1; padding: 2px 8px; border-radius: 4px;";
          break;
        case "warn":
          style = "color: #fff; background-color: #ffc107; padding: 2px 8px; border-radius: 4px;";
          break;
        case "error":
          style = "color: #fff; background-color: #dc3545; padding: 2px 8px; border-radius: 4px;";
          break;
        case "success":
          style = "color: #fff; background-color: #28a745; padding: 2px 8px; border-radius: 4px;";
          break;
        default:
          style = baseStyles;
      }
      const timestamp = performance.now().toFixed(3);
      const logFn = (console[level] && typeof console[level] === "function") ? console[level] : console.log;
      logFn.call(console, "%cGH - Free Tiers", style, `[${timestamp}ms]`, ...args);
    }

    // Attach logger.debug/info/warn/error/success, using loggingLevel threshold
    LOG_LEVELS.forEach(function (level) {
      logger[level] = function () {
        if (!window.console) return;
        
        // Get current logging level from window.freeTiers or use defaultLevel
        const currentLevel = (window.freeTiers && window.freeTiers.loggingLevel) || defaultLevel;
        const currentIdx = LOG_LEVEL_INDEX[currentLevel] ?? 0;
        const thisIdx = LOG_LEVEL_INDEX[level] ?? 0;
        
        // Only log if this level is >= loggingLevel
        if (thisIdx >= currentIdx) {
          loggerBase(level, ...arguments);
        }
      };
    });

    return logger;
  }

  function formatMoney(cents, format = "") {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
    function defaultTo(value2, defaultValue) {
      return value2 == null || value2 !== value2 ? defaultValue : value2;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ",");
      decimal = defaultTo(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    let value = "";
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  }

  /**
   * Centralized cart update method that handles both single and batch updates
   * @param {Object|number} updates - Either an updates object {key: quantity} or a single variant ID
   * @param {number} quantity - If updates is a number, this is the quantity for that variant
   * @param {string} reason - Reason for the update (for logging)
   * @returns {Object|null} Updated cart object or null if failed
   */
  async function updateCart(updates, quantity = null, reason = 'unknown') {
    logger.info([`updateCart - ${reason}`, updates, quantity]);
    
    try {
      const params = new URLSearchParams();
      
      // Handle both single item and batch updates
      if (typeof updates === 'number' && quantity !== null) {
        // Single item update: updateCart(variantId, quantity, reason)
        params.append(`updates[${updates}]`, quantity);
      } else if (typeof updates === 'object' && updates !== null) {
        // Batch update: updateCart({key1: qty1, key2: qty2}, null, reason)
        for (const key in updates) {
          params.append(`updates[${key}]`, updates[key]);
        }
      } else {
        logger.error(['updateCart - Invalid updates parameter', { updates, quantity, reason }]);
        return await getCart(); // Fallback to current cart
      }

      const response = await fetch('/cart/update.js', {
        body: params.toString(),
        credentials: "same-origin",
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        }
      }).catch((e) => {
        logger.error(['updateCart - Network error', e.message]);
        return null; // Signal fetch failure
      });

      if (response && response.ok) {
        const cart = await response.json();
        logger.debug(['updateCart success - using returned cart object']);
        return cart;
      } else {
        const status = response ? response.status : 'network failure';
        logger.error(['updateCart - API error', { status, reason }]);
        // Fallback to getCart() if update failed
        return await getCart();
      }

    } catch (e) {
      logger.error(['updateCart - Unexpected error', e.message, { updates, quantity, reason }]);
      // Always try to return current cart state instead of throwing
      try {
        return await getCart();
      } catch (fallbackError) {
        logger.error(['updateCart - Even getCart() failed', fallbackError.message]);
        return null; // Complete failure, but don't throw
      }
    }
  }

  async function getCart() {
    try {
      const response = await fetch(
        `${window.themeVariables.routes.cartUrl}.js`
      ).catch((e) => {
        logger.error(['getCart - Network error', e.message]);
        return null;
      });

      if (response && response.ok) {
        let cart = await response.json();
        logger.info(['getCart()', cart]);

        if (cart.total_discount > 0) {
          logger.warn('discount exists that trumps bundler');
          return cart;
        }

        if (window.bndlr && window.bndlr.updateCartWithDiscounts) {
          const bndlrResult = window.bndlr.updateCartWithDiscounts(cart);

          if (bndlrResult.discounted_cart_object) {
            logger.debug(['replacing cart with bndlr cart', bndlrResult.discounted_cart_object]);
            return bndlrResult.discounted_cart_object;
          }
        }
        return cart;
      } else {
        const status = response ? response.status : 'network failure';
        logger.error(['getCart - API error', status]);
        return null;
      }

    } catch (e) {
      logger.error(['getCart - Unexpected error', e.message]);
      return null; // Fail gracefully, don't throw
    }
  }

  async function checkCart(reason='unknown', cart = null) {
    // Only fetch cart if not provided
    if (!cart) {
      cart = await getCart();
      if (!cart) {
        logger.error(['checkCart - Could not retrieve cart, aborting']);
        return null;
      }
    }

    if (window.freeTiers.singleThreadInUse === true) {
      logger.warn('Prevented race condition');
      return cart;
    }
    window.freeTiers.singleThreadInUse = true;

    logger.info([`checkCart - ${reason}`, cart]);

    const qualifiedItems = await getQualifiedItems(cart);
    const cartTotal = qualifiedItems.reduce((acc, item) => acc + item.discounted_price * item.quantity, 0);
    logger.debug(['Qualified Cart Total', cartTotal]);

    // Declare hasChanges at function scope so it's accessible everywhere
    let hasChanges = false;

    try {
      const thresholds = window.freeTiers.thresholds;
      
      // Collect all cart updates in a single object
      const updates = {};

      const cartItems = cart.items.map((item) => {
        logger.debug(['cartItem', item]);
        return {
          item,
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          key: item.key,
          id: item.id,
        }
      });
      logger.debug(['cartItems', cartItems]);

      for (const thresholdKey in thresholds) {
        const threshold = thresholds[thresholdKey];

        if (threshold.variant_id === 0) {
          continue;
        }

        const product = cartItems.find((item) => {
          return item.variant_id === threshold.variant_id;
        });

        if (cartTotal >= threshold.amount) {
          logger.info([`checkCart - Threshold ${thresholdKey} met`]);

          if (!product || product.quantity < 1) {
            logger.success([`checkCart - ACTION - Adding item for threshold ${thresholdKey}`, threshold]);
            updates[threshold.variant_id] = 1;
            hasChanges = true;
          }
          if (product && product.quantity > 1) {
            logger.success([`checkCart - ACTION - Reducing qty for threshold ${thresholdKey}`, threshold]);
            updates[product.key] = 1;
            hasChanges = true;
          }
        } else {
          logger.info([`checkCart - Threshold ${thresholdKey} not met`]);
          if (product) {
            logger.success([`checkCart - ACTION - Removing item for threshold ${thresholdKey}`, threshold]);
            updates[product.key] = 0;
            hasChanges = true;
          }
        }
      }

      // Execute all updates in a single API call
      if (hasChanges) {
        const updatedCart = await updateCart(updates, null, `checkCart-${reason}`);
        if (updatedCart) {
          cart = updatedCart;
        } else {
          logger.error(['checkCart - Cart update failed, continuing with current cart']);
        }
      }
    } catch (e) {
      logger.error(['checkCart:ERROR', e]);
    }

    // for CartDrawer, AjaxCart to use
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));

    // for CartCount to use - use the cart we already have
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
      bubbles: true,
      detail: {
        cart: cart
      }
    }));

    // Update DOM to reflect current threshold state
    // Pre-calculate DOM state for fast application after cart refresh
    const domState = calculateDOMState(cart);
    window.freeTiers.pendingDOMState = domState;
    logger.debug(['Stored pendingDOMState for cart:refreshed', 'hasChanges:', hasChanges, 'domState:', !!domState]);
    
    // Apply immediately if no cart changes were made
    if (!hasChanges) {
      applyDOMState(domState, reason);
    }
    // Otherwise, stored state will be applied by cart:refreshed event handler

    window.freeTiers.singleThreadInUse = false;
    return cart;
  }

  function adjustCurrency() {
    logger.info('adjustCurrency');
    if (!Shopify?.currency?.rate) {
      return false;
    }
    const rate = parseFloat(Shopify.currency.rate);
    logger.debug(['USD tiers:', window.freeTiers]);

    if (rate === 1) {
      logger.debug('Currency is USD, skipping currency conversion');
      window.freeTiers.hasAdjustedForCurrency = true;
      return true;
    }

    const thresholds = window.freeTiers.thresholds;
    for (const thresholdKey in thresholds) {
      const threshold = thresholds[thresholdKey];
      threshold['amount'] = (threshold['amount'] * rate).toFixed(2);
    }

    window.freeTiers.hasAdjustedForCurrency = true;
    logger.info([`adjustedCurrency - New Tier thresholds, rate: ${rate.toFixed(3)}`, window.freeTiers]);
    return true;
  }

  // Cache for DOM elements to avoid repeated queries
  const domCache = {};

  function getCachedElement(selector) {
    // Don't cache free-tier elements since they get replaced by AJAX cart reloads
    if (selector.includes('#gh-free-tiers')) {
      return document.querySelector(selector);
    }
    
    // Cache other stable elements normally
    if (!domCache[selector]) {
      domCache[selector] = document.querySelector(selector);
    }
    return domCache[selector];
  }

  function clearDOMCache() {
    Object.keys(domCache).forEach(key => delete domCache[key]);
  }

  async function getQualifiedItems(cart) {
    const qualifiedItems = [];

    // Validate cart object and items array
    if (!cart) {
      logger.error(['getQualifiedItems - Cart is null/undefined']);
      return qualifiedItems;
    }

    if (!cart.items || !Array.isArray(cart.items)) {
      logger.error(['getQualifiedItems - Cart.items is not an array', cart]);
      return qualifiedItems;
    }

    // Use the pre-computed Set of excluded product handles
    const excludedHandlesSet = window.freeTiers.excludedHandlesSet || new Set();
    
    for (const item of cart.items) {
      const handle = item.handle || item.product_handle;
      
      if (!handle) {
        logger.warn('No handle found for item, assuming qualified:', item.product_id);
        qualifiedItems.push(item);
        continue;
      }

      // Check if this product handle is in the excluded list
      if (excludedHandlesSet.has(handle)) {
        logger.debug(['Item excluded due to no-free-tier tag:', handle]);
      } else {
        qualifiedItems.push(item);
      }
    }

    logger.info([`getQualifiedItems - ${qualifiedItems.length} of ${cart.items.length} Items Qualified`]);
    return qualifiedItems;
  }

  function calculateDOMState(cart) {
    logger.debug(['calculateDOMState - Starting calculation']);
    
    // Use qualified items total (excluding no-free-tier products) like checkCart does
    const qualifiedItems = cart.items.filter(item => {
      const handle = item.handle || item.product_handle;
      if (!handle) return true; // assume qualified if no handle
      const excludedHandlesSet = window.freeTiers.excludedHandlesSet || new Set();
      return !excludedHandlesSet.has(handle);
    });
    
    const cartTotal = qualifiedItems.reduce((acc, item) => acc + item.discounted_price * item.quantity, 0);
    logger.debug(['calculateDOMState - Cart total:', cartTotal]);
    
    if (cartTotal === 0) {
      logger.debug(['calculateDOMState - Cart total is 0, no DOM updates needed']);
      return null; // No DOM updates needed
    }

    const thresholds = window.freeTiers.thresholds;
    const thresholdsSorted = Object.keys(thresholds).sort((a, b) => thresholds[a].amount - thresholds[b].amount);

    let success = true;
    let lastThresholdKey = 0;
    const tierStates = {};
    let progressState = null;
    let goalDescState = null;

    // Calculate tier states
    for (const thresholdKey of thresholdsSorted) {
      const threshold = thresholds[thresholdKey];
      
      if (threshold['amount'] <= cartTotal) {
        lastThresholdKey = thresholdKey;
        tierStates[thresholdKey] = { activated: true };
      } else {
        tierStates[thresholdKey] = { activated: false };
        
        // Calculate progress for first unmet threshold
        if (!progressState) {
          const lastThreshold = thresholds[lastThresholdKey] || { 'amount': '0' };
          const lastThresholdAmount = lastThreshold['amount'];
          const goalAmountWithinThreshold = threshold['amount'] - lastThresholdAmount;
          const cartTotalWithinThreshold = cartTotal - lastThresholdAmount;
          const percentCompleted = Math.round(parseFloat(cartTotalWithinThreshold / goalAmountWithinThreshold) * 100, 2);
          
          if (percentCompleted !== 0 && percentCompleted !== 100) {
            progressState = {
              thresholdKey,
              percentage: percentCompleted
            };
          }
        }
        success = false;
      }
    }

    // Calculate goal description state
    if (success) {
      goalDescState = { type: 'success' };
    } else {
      const nextThreshold = parseInt(lastThresholdKey) + 1;
      if (thresholds[nextThreshold]) {
        const amountNeeded = thresholds[nextThreshold]['amount'] - cartTotal;
        goalDescState = {
          type: 'next',
          thresholdKey: nextThreshold,
          amountNeeded: formatMoney(amountNeeded)
        };
      }
    }

    logger.debug(['calculateDOMState - Final state:', {
      cartTotal,
      tierStates,
      progressState,
      goalDescState
    }]);

    return {
      cartTotal,
      tierStates,
      progressState,
      goalDescState
    };
  }

  function applyDOMState(domState, reason = 'apply-state') {
    if (!domState) {
      logger.debug('No DOM state to apply');
      return;
    }

    logger.info([`applyDOMState - Reason: ${reason}`, domState]);
    
    const freeTiersContainer = getCachedElement('#gh-free-tiers');
    if (!freeTiersContainer) {
      logger.debug('gh-free-tiers not found, skipping');
      return;
    }

    // Apply tier states
    Object.entries(domState.tierStates).forEach(([thresholdKey, state]) => {
      const tierDom = getCachedElement(`#gh-free-tiers .goal-container[free-tier="${thresholdKey}"]`);
      if (tierDom) {
        if (state.activated) {
          tierDom.classList.add('activated');
          logger.debug(['Adding activated class to threshold', thresholdKey]);
        } else {
          tierDom.classList.remove('activated');
          logger.debug(['Removing activated class from threshold', thresholdKey]);
        }
      }
    });

    // Apply progress state
    if (domState.progressState) {
      const { thresholdKey, percentage } = domState.progressState;
      const tierDom = getCachedElement(`#gh-free-tiers .goal-container[free-tier="${thresholdKey}"]`);
      
      if (tierDom) {
        // Move progress bar to current threshold if needed
        if (!tierDom.querySelector('.goal-feature .progress')) {
          const progressElements = freeTiersContainer.querySelectorAll('.goal-feature .progress');
          progressElements.forEach(element => element.remove());
          
          const progressElement = getCachedElement('#gh-free-tiers > .progress');
          if (progressElement && tierDom.querySelector('.goal-feature')) {
            tierDom.querySelector('.goal-feature').prepend(progressElement);
          }
        }

        const tierProgress = tierDom.querySelector('.progress');
        const tierProgressBar = tierProgress?.querySelector('.progress-bar');
        if (tierProgressBar) {
          tierProgressBar.style.width = `${percentage}%`;
          tierProgress.classList.remove('hidden');
          logger.debug(['Setting progress bar width to', percentage + '%', 'for threshold', thresholdKey]);
        }
      }
    }

    // Clear all goal descriptions first
    const allGoalDescs = freeTiersContainer.querySelectorAll('.goal-desc');
    allGoalDescs.forEach(desc => desc.classList.remove('activated'));

    // Apply goal description state
    if (domState.goalDescState) {
      const { type, thresholdKey, amountNeeded } = domState.goalDescState;
      
      if (type === 'success') {
        const successElement = getCachedElement(`#gh-free-tiers .goal-desc[free-tier="success"]`);
        if (successElement) {
          successElement.classList.add('activated');
          logger.debug(['Success element activated']);
        }
      } else if (type === 'next') {
        const tierPhrase = getCachedElement(`#gh-free-tiers .goal-desc[free-tier="${thresholdKey}"]`);
        if (tierPhrase) {
          tierPhrase.classList.add('activated');
          
          // Store original template if not already stored
          if (!tierPhrase.dataset.originalText) {
            tierPhrase.dataset.originalText = tierPhrase.textContent;
          }
          
          // Replace amount in text
          const newText = tierPhrase.dataset.originalText.replace('[amount]', amountNeeded);
          const pTag = tierPhrase.querySelector('p');
          if (pTag) {
            pTag.textContent = newText;
          } else {
            tierPhrase.textContent = newText;
          }
          
          logger.debug(['Tier phrase activated for threshold', thresholdKey, 'with amount', amountNeeded]);
        }
      }
    }
  }

  async function refreshDOM(reason='unknown', cart = null) {
    logger.info([`refreshDOM - Reason: ${reason}`, 'pendingDOMState exists:', !!window.freeTiers.pendingDOMState]);
    
    // Fast path: use stored DOM state if available
    if (window.freeTiers.pendingDOMState && reason === 'cart:refreshed') {
      logger.debug(['Using stored DOM state for fast refresh']);
      applyDOMState(window.freeTiers.pendingDOMState, reason);
      window.freeTiers.pendingDOMState = null;
      return;
    }
    
    // Only fetch cart if not provided
    if (!cart) {
      cart = await getCart();
      if (!cart) {
        logger.error(['refreshDOM - Could not retrieve cart, aborting']);
        return;
      }
    }
    
    // Calculate and apply DOM state (unified code path)
    const domState = calculateDOMState(cart);
    applyDOMState(domState, reason);
  }

  // Check cart for free tiers on change.js
  async function updateCartChangeWithFreeTiers(changePayload) {
    logger.info(['updateCartChangeWithFreeTiers', changePayload]);

    const cart = await getCart();
    if (!cart) {
      logger.error(['updateCartChangeWithFreeTiers - Could not retrieve cart, aborting']);
      return null;
    }
    let updates = {};

    // Simulate the cart after the change
    let simulatedItems = cart.items.map(item => ({ ...item }));
    let found = false;

    // If payload has line, update by line; else by id
    if (changePayload.line) {
      logger.debug(['changePayload.line', changePayload.line]);

      const idx = parseInt(changePayload.line, 10) - 1;
      if (simulatedItems[idx]) {
        simulatedItems[idx].quantity = changePayload.quantity;
        updates[simulatedItems[idx].key] = changePayload.quantity;
        found = true;
      }
    }
    if (!found && changePayload.id) {
      logger.debug(['changePayload.id', changePayload.id]);

      for (let item of simulatedItems) {
        logger.debug(['simulatedItem', item]);
        // sometimes the id is the variant id, sometimes it's the key
        if (item.id == changePayload.id || item.key == changePayload.id) {
          updates[item.key] = changePayload.quantity;
          item.quantity = changePayload.quantity;
          break;
        }
      }
    }

    if (Object.keys(updates).length === 0) {
      // if no tier changes, continue with the original fetch
      return;
    }

    // Remove any items with quantity 0
    simulatedItems = simulatedItems.filter(item => item.quantity > 0);

    // Calculate new qualified total (excluding free items)
    const qualifiedItems = await getQualifiedItems({ items: simulatedItems });
    logger.debug(['qualifiedItems', qualifiedItems]);
    const qualifiedTotal = qualifiedItems.reduce((acc, item) => acc + item.discounted_price * item.quantity, 0);
    logger.debug(['qualifiedTotal', qualifiedTotal]);

    // For each free tier, check if the simulated cart qualifies for the free item
    for (const thresholdKey in window.freeTiers.thresholds) {
      const threshold = window.freeTiers.thresholds[thresholdKey];
      const freeVariantId = threshold.variant_id;
      if (!freeVariantId || freeVariantId === 0) continue;

      // Find the free item in the simulated cart
      const simulatedProduct = simulatedItems.find(item => item.variant_id == freeVariantId);

      // If qualifiedTotal >= threshold.amount, ensure free item is present with quantity 1
      if (qualifiedTotal >= threshold.amount) {
        logger.debug(['threshold met!', freeVariantId]);
        if (!simulatedProduct || simulatedProduct.quantity < 1) {
          // Add or update to quantity 1
          updates[freeVariantId] = 1;
        }
        if (simulatedProduct && simulatedProduct.quantity > 1) {
          // Too many, set to 1
          updates[simulatedProduct.key] = 1;
        }
      } else {
        // Otherwise, remove the free item if present
        logger.debug(['threshold NOT met.', freeVariantId]);
        if (simulatedProduct) {
          updates[simulatedProduct.key] = 0;
        }
      }
    }

    // if we have more than just the change, we need to update the cart ourselves
    if (Object.keys(updates).length > 1) {
      logger.debug(['adjusting free tier items via updateCart', updates]);
      const updatedCart = await updateCart(updates, null, 'updateCartChangeWithFreeTiers');
      
      if (updatedCart) {
        // Return a mock response object that matches the expected interface
        return {
          ok: true,
          json: async () => updatedCart
        };
      } else {
        logger.error(['updateCartChangeWithFreeTiers - Cart update failed']);
        return {
          ok: false,
          json: async () => null
        };
      }
    }

    logger.debug(['proceeding with original change']);
  }

  // Intercept fetch for /cart/change.js
  function interceptFetch() {
    const origFetch = window.fetch;
    window.fetch = async function (input, init) {
      try {
        let url = (typeof input === 'string') ? input : input.url;
        if (url && url.indexOf(window.themeVariables.routes.cartChangeUrl) !== -1 && init && init.method && init.method.toUpperCase() === 'POST') {
          let body = init.body;
          let isJson = false;
          let payload = {};
          if (init.headers && (init.headers['Content-Type'] === 'application/json' || (init.headers.get && init.headers.get('Content-Type') === 'application/json'))) {
            isJson = true;
            payload = JSON.parse(body);
          } else if (typeof body === 'string') {
            // Try to parse as form data
            try {
              payload = Object.fromEntries(new URLSearchParams(body));
              // Convert numeric fields
              if (payload.quantity) payload.quantity = parseInt(payload.quantity, 10);
              if (payload.line) payload.line = parseInt(payload.line, 10);
            } catch (e) { }
          }
          // Only run for single change (not already a batch)
          if (!payload.changes) {
            const resp = await updateCartChangeWithFreeTiers(payload);

            logger.debug(['updateCartChangeWithFreeTiers response', resp]);

            if (resp.ok) {
              return resp;
            }
          }
        }
      } catch (e) {
        // fail silently, fallback to original fetch
      }
      return origFetch.apply(this, arguments);
    };
  }

  async function init() {
    window.freeTiers = {
      ...window.freeTiers,
      checkCart: checkCart,
      loggingLevel: 'error',
      hasAdjustedForCurrency: false,
      singleThreadInUse: false,
      pendingDOMState: null,
      clearCaches: () => {
        clearDOMCache();
      }
    };

    // Create the logger instance
    logger = createLogger(window.freeTiers.loggingLevel);
    logger.info('init');

    // Convert excluded product handles array to Set for O(1) lookup performance
    if (window.freeTiers.noFreeTierProductHandles && Array.isArray(window.freeTiers.noFreeTierProductHandles)) {
      window.freeTiers.excludedHandlesSet = new Set(window.freeTiers.noFreeTierProductHandles);
      logger.debug(['Converted', window.freeTiers.noFreeTierProductHandles.length, 'excluded product handles to Set']);
    } else {
      window.freeTiers.excludedHandlesSet = new Set();
      logger.debug('No excluded product handles found, using empty Set');
    }

    // Optimize currency conversion - only do it once
    if (!window.freeTiers.hasAdjustedForCurrency) {
      adjustCurrency();
    }

    // check cart for free tiers on init
    await checkCart('init');

    window.addEventListener("pageshow", async function (event) {
      // back button support from checkout
      if (event.persisted) {
        logger.info('pageshow event received, refreshing cart...');
        await checkCart('pageshow');
      }
    });

    // Listen for theme's cart refresh completion
    document.addEventListener('cart:refreshed', async function(event) {
      logger.debug(['cart:refreshed event received', 'pendingDOMState exists:', !!window.freeTiers.pendingDOMState]);
      
      // Use pre-calculated DOM state for instant updates
      if (window.freeTiers.pendingDOMState) {
        logger.debug(['Applying stored DOM state after cart refresh']);
        applyDOMState(window.freeTiers.pendingDOMState, 'cart:refreshed');
        window.freeTiers.pendingDOMState = null; // Clear after use
      } else {
        logger.debug(['No stored DOM state, falling back to full refresh']);
        await refreshDOM('cart:refreshed');
      }
    });

    // Watch for changes to our free-tier elements as a safety net
    function setupFreeTierObserver() {
      const targetNode = document.querySelector('#gh-free-tiers');
      if (!targetNode) {
        logger.debug('Free tiers container not found, will retry later');
        return false;
      }

      const observer = new MutationObserver(function(mutations) {
        let shouldRefresh = false;
        
        mutations.forEach(function(mutation) {
          // Only trigger on significant changes (element additions/removals or class changes)
          if (mutation.type === 'childList' || 
              (mutation.type === 'attributes' && mutation.attributeName === 'class')) {
            shouldRefresh = true;
          }
        });

        if (shouldRefresh) {
          logger.debug(['Free tier elements mutated, refreshing DOM']);
          // Small delay to let any batch changes complete
          setTimeout(() => {
            refreshDOM('mutation-observer');
          }, 50);
        }
      });

      observer.observe(targetNode, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });

      logger.debug('Free tier MutationObserver setup complete');
      return true;
    }

    // Setup observer after a brief delay
    setTimeout(() => {
      setupFreeTierObserver();
    }, 200);

    interceptFetch();
  }

  // relies on Shopify admin config to be enabled
  if (window.freeTiers?.useFreeTiers === 'true') {
    init();
  }
})();
